import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { PageBase } from '../../page.base';
import { AuthService } from '../../services/auth.service';
import { DeviceService } from '../../services/device.service';
import { RequestsServices } from '../../services/requests.service';
import { ToasterService } from '../../services/toaster.service';
import { getUser } from '../../store/selectors/auth.selectors';
import { updateUser } from '../../store/actions/auth.actions';
import { ModalCoreComponent } from '../modal-core/modal.component';

export const NOVE_NOVE_ABASTECE_GROUP = 'nove_nove_abastece';

// Mesmo intervalo usado no app entre a atualização do perfil e o resgate do benefício.
const REDEEM_DELAY_MS = 750;

/**
 * Fluxo de resgate do 99Abastece (group nove_nove_abastece):
 * - desktop: "Ativar benefício" abre o modal com QR code para ativar pelo app;
 * - mobile/tablet: "Gerar cupom" confirma o celular do usuário, atualiza o perfil
 *   e resgata o link do benefício.
 */
@Component({
  selector: 'ng-nove-nove-checkout',
  templateUrl: './nove-nove-checkout.component.html',
  styleUrls: ['./nove-nove-checkout.component.scss'],
  providers: [RequestsServices],
  encapsulation: ViewEncapsulation.None,
})
export class NoveNoveCheckoutComponent extends PageBase implements OnInit, OnDestroy {

  @ViewChild('modalPhone') modalPhone: ModalCoreComponent;
  @ViewChild('modalApp') modalApp: ModalCoreComponent;

  @Input() establishment: any;
  @Output() generateCouponClick = new EventEmitter<void>();
  @Output() activateBenefitClick = new EventEmitter<void>();
  @Output() desktopBenefitClick = new EventEmitter<void>();

  public modalConfig = { class: 'modal-99checkout-dialog' };
  public modalAppConfig = { class: 'modal-99abastece-dialog' };
  public isDesktop = false;
  public phone = '';
  public hasSavedPhone = false;
  public isLoading = false;

  private user: any;
  private userSubscription: Subscription;
  private deviceSubscription: Subscription;

  constructor(
    private store: Store,
    private authService: AuthService,
    private request: RequestsServices,
    private toaster: ToasterService,
    private deviceService: DeviceService,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.userSubscription = this.store.pipe(select(getUser)).subscribe(user => this.user = user);
    this.deviceSubscription = this.deviceService.isDesktop$.subscribe(isDesktop => this.isDesktop = isDesktop);
  }

  public ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }
  }

  public get isNoveNoveAbastece(): boolean {
    return Boolean(this.establishment) && this.establishment.group === NOVE_NOVE_ABASTECE_GROUP;
  }

  public get isPhoneComplete(): boolean {
    return this.onlyDigits(this.phone).length === 11;
  }

  public onBenefitButtonClick(): void {
    if (this.isDesktop) {
      this.openAppModal();
    } else {
      this.openModal();
    }
  }

  public openAppModal(): void {
    this.desktopBenefitClick.emit();
    this.modalApp.openModal();
  }

  public closeAppModal(): void {
    this.modalApp.closeModal();
  }

  public openModal(): void {
    this.generateCouponClick.emit();

    const savedPhone = this.user && this.user.phone ? String(this.user.phone) : '';
    this.hasSavedPhone = Boolean(savedPhone);
    this.phone = this.formatPhone(savedPhone);
    this.modalPhone.openModal();
  }

  public closeModal(): void {
    this.modalPhone.closeModal();
  }

  public async activateBenefit(): Promise<void> {
    if (this.isLoading || !this.isPhoneComplete) {
      return;
    }

    this.activateBenefitClick.emit();
    this.isLoading = true;

    // Abre a aba de forma síncrona ao clique para não ser bloqueada como popup.
    const benefitWindow = window.open('', '_blank');

    try {
      await this.updatePhone();
      await this.delay(REDEEM_DELAY_MS);

      const link = await this.redeemBenefit();
      if (!link) {
        throw new Error('99Abastece link not found');
      }

      if (benefitWindow) {
        benefitWindow.location.href = link;
      } else {
        window.location.href = link;
      }
      this.closeModal();
    } catch {
      if (benefitWindow) {
        benefitWindow.close();
      }
      this.handleError();
    } finally {
      this.isLoading = false;
    }
  }

  private async updatePhone(): Promise<void> {
    const user = await this.authService.updateMe({ phone: this.phone }).toPromise();
    if (user) {
      this.store.dispatch(updateUser({ user }));
    }
  }

  private async redeemBenefit(): Promise<string | null> {
    const response: any = await this.request.post('abastece99', {}, '', '/users');
    if (typeof response === 'string') {
      return response;
    }

    return (response && (response.appUrl || (response.data && response.data.appUrl))) || null;
  }

  private handleError(): void {
    this.toaster.info('Atenção!', 'Não foi possível ativar o benefício. Tente novamente.', false, 4000);
  }

  private formatPhone(value: string): string {
    const digits = this.onlyDigits(value);
    if (digits.length !== 11) {
      return value;
    }

    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }

  private onlyDigits(value: string): string {
    return (value || '').replace(/\D/g, '');
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
